import { Vertical, Horizontal, GraviGrid, Texto, BBDTag } from '@gravitate-js/excalibrr'
import { Drawer } from 'antd'
import type { CellStyle, ColDef, GridApi, ICellRendererParams, IFilter } from 'ag-grid-community'
import type * as React from 'react'
import { useMemo, useRef, type MutableRefObject } from 'react'
import type { RateHistoryDrawerProps } from '../types'
import { EditRateButton } from './EditRateButton'
import { DeleteRateButton } from './DeleteRateButton'
import type { SurchargeRate } from '@api/useFreight/types'
import { RateStatusTag } from '@components/Freight/Ratebooks/Drill-in/components/RateStatusTag'
import { SurchargeScheduleButton } from '@components/Freight/Ratebooks/Drill-in/components/ScheduleRateButton'
import { formatSurchargeValue } from '../utils/rateUtils'
import dayjs from 'dayjs'
import { getMostRecentActiveRateId } from '../utils'

interface GridContext {
  surchargeType: string
  mostRecentActiveRateId: string | null
  onRateChange: (() => void) | undefined
  surcharge: RateHistoryDrawerProps['surcharge']
}

// Renderer Components
const RateRenderer: React.FC<ICellRendererParams<SurchargeRate>> = ({ value, context }) => {
  if (value === null || value === undefined) return <Texto appearance='hint'>N/A</Texto>
  return <BBDTag theme2>{formatSurchargeValue(value, context.surchargeType || 'Percentage')}</BBDTag>
}

const StatusRenderer: React.FC<ICellRendererParams<SurchargeRate>> = ({ data, context }) => {
  if (!data) return null
  return <RateStatusTag rate={data} isMostRecentActive={data.id === context.mostRecentActiveRateId} />
}

const DateRenderer: React.FC<ICellRendererParams<SurchargeRate>> = ({ value }) => {
  if (!value) return null
  return <Texto>{dayjs(value.toString()).format('MMM D, YYYY')}</Texto>
}

const ToDateRenderer: React.FC<ICellRendererParams<SurchargeRate>> = ({ value }) => {
  return value ? (
    <Texto>{dayjs(value.toString()).format('MMM D, YYYY')}</Texto>
  ) : (
    <Texto appearance='hint'>Indefinite</Texto>
  )
}

const ActionsRenderer: React.FC<ICellRendererParams<SurchargeRate>> = ({ data, context }) => {
  if (!data) return null

  const now = dayjs()
  const effectiveFrom = dayjs(data.effective_from as string)
  const isScheduled = effectiveFrom.isAfter(now)

  return (
    <Horizontal gap={8} verticalCenter style={{ overflow: 'visible', width: '100%' }}>
      <EditRateButton rate={data} surcharge={context.surcharge} onSuccess={context.onRateChange} />
      {isScheduled && <DeleteRateButton rate={data} surcharge={context.surcharge} onSuccess={context.onRateChange} />}
    </Horizontal>
  )
}

/**
 * Component for displaying and managing rate history for a surcharge
 */
export function RateHistoryDrawer({ surcharge, onRateChange, open, onClose }: RateHistoryDrawerProps) {
  const gridRef = useRef<GridApi>(null)

  // Calculate the most recent active rate ID
  const mostRecentActiveRateId = useMemo(
    () => getMostRecentActiveRateId(surcharge.rates, surcharge.disabled),
    [surcharge.rates, surcharge.disabled]
  )

  // Define column definitions for the rate history grid
  const columnDefs = useMemo<ColDef<SurchargeRate>[]>(
    () => [
      {
        headerName: 'Status',
        minWidth: 120,
        cellRenderer: StatusRenderer,
      },
      {
        headerName: 'Rate',
        field: 'surcharge',
        minWidth: 100,
        cellStyle: { textAlign: 'right' } as CellStyle,
        type: 'numericColumn',
        cellRenderer: RateRenderer,
      },
      {
        headerName: 'From',
        field: 'effective_from',
        minWidth: 100,
        cellRenderer: DateRenderer,
      },
      {
        headerName: 'To',
        field: 'effective_to',
        minWidth: 100,
        cellRenderer: ToDateRenderer,
      },
      {
        headerName: '',
        minWidth: 80,
        width: 80,
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        } as CellStyle,
        cellRenderer: ActionsRenderer,
      },
    ],
    []
  )

  const context = useMemo<GridContext>(
    () => ({
      surchargeType: surcharge.type || 'Percentage',
      mostRecentActiveRateId,
      onRateChange,
      surcharge,
    }),
    [surcharge, mostRecentActiveRateId, onRateChange]
  )

  return (
    <Drawer
      title={
        <Vertical gap={8}>
          <Texto category='h5' weight='600'>
            Rate History
          </Texto>
          <Horizontal gap={8}>
            <Texto>Region:</Texto>
            <Texto appearance='hint'>{surcharge.freight_region || 'All Regions'}</Texto>
            <Texto>Type:</Texto>
            <BBDTag theme2>{surcharge.type || 'Percentage'}</BBDTag>
          </Horizontal>
        </Vertical>
      }
      open={open}
      onClose={onClose}
      width={600}
    >
      <Vertical gap={16}>
        <Horizontal justifyContent='flex-end'>
          <SurchargeScheduleButton surcharge={surcharge} onSuccess={onRateChange} />
        </Horizontal>

        <GraviGrid<SurchargeRate, IFilter>
          key={`rate-history-${surcharge._id}-${surcharge.rates?.length}`}
          storageKey='surcharge_rate_history'
          rowData={surcharge.rates || []}
          agPropOverrides={{
            getRowId: (params) => params.data.id,
            rowGroupPanelShow: 'never',
            rowHeight: 40,
            columnDefs: columnDefs,
            context: context,
          }}
          controlBarProps={{
            title: `Rate History (${surcharge.rates?.length || 0})`,
            customSearchBar: <div />,
          }}
          externalRef={gridRef as MutableRefObject<GridApi>}
        />
      </Vertical>
    </Drawer>
  )
}
